import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide'
import { IGatsbyImageData } from 'gatsby-plugin-image'
import React, { FC } from 'react'
import { MenuCarouselCard } from 'src/components/molecules/cards/menuCarouselCard'
import { PrismicLinkType } from 'src/typings/generated/graphql'
import { generateRandomId } from 'src/utils/domHelper'
import { useMediaQuery } from 'src/utils/useMediaQuery'
import * as Styles from './menuCarousel.module.scss'

/**
 * Represents a single item in the menu carousel.
 */
export type MenuCarouselItem = {
  /**
   * The name of the menu item.
   */
  name?: string
  /**
   * The image of the menu item.
   */
  image: IGatsbyImageData
  /**
   * The alternative text for the image of the menu item.
   */
  imageAlt?: string
  /**
   * The description of the menu item. Usually a short paragraph describing the item's ingredients, serving size, or
   * other relevant information.
   */
  description?: string
  /**
   * Text representing the number of calories in the menu item.
   */
  calories?: string
  /**
   * The link associated with the menu item.
   */
  link?: PrismicLinkType
  /**
   * The text for the call-to-action button associated with the menu item.
   */
  ctaText?: string
}

/**
 * Props for the `MenuCarousel` component.
 */
export type MenuCarouselProps = {
  anchor?: string
  items: MenuCarouselItem[]
}

/**
 * A carousel component that displays a list of clickable menu items.
 */
export const MenuCarousel: FC<MenuCarouselProps> = ({
  anchor = generateRandomId(),
  items,
}) => {
  const isWideViewport = useMediaQuery('(min-width: 62em)')
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  const shouldRenderContent = items && items.length > 0

  const splideOptions = {
    fixedWidth: 232,
    padding: isWideViewport ? 122 : 20,
    gap: 10,
    autoHeight: true,
    arrows: false,
    pagination: false,
    reducedMotion: prefersReducedMotion,
  }

  return (
    <section id={anchor} className={Styles.menuCarousel}>
      {shouldRenderContent && (
        <div className={Styles.content}>
          <Splide
            hasTrack={false}
            options={splideOptions}
            className={Styles.trackWrapper}
          >
            <SplideTrack className={Styles.track}>
              {items.map(item => (
                <SplideSlide key={item.name} className={Styles.trackItem}>
                  <MenuCarouselCard
                    name={item.name}
                    image={item.image}
                    imageAlt={item.imageAlt}
                    description={item.description}
                    calories={item.calories}
                    link={item.link}
                    ctaText={item.ctaText}
                    expandOnHover={isWideViewport}
                  />
                </SplideSlide>
              ))}
            </SplideTrack>
          </Splide>
        </div>
      )}
    </section>
  )
}
